import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { AiChatbotCategoryOption } from '../../models/ai-chatbot-category-option';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ai-chatbot-category-chip',
  templateUrl: './ai-chatbot-category-chip.component.html',
  styleUrl: './ai-chatbot-category-chip.component.scss',
  standalone: false,
})
export class AiChatbotCategoryChipComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  @Input() label: string = '';
  @Input() icon: string = '';
  @Input() options: AiChatbotCategoryOption[] = [];
  @Input() isMultiSelect: boolean = false;
  @Input() isDisplayOnly: boolean = false;

  @Input() selectedValue: string;
  @Input() selectedValues: Set<string> = new Set();
  selectedOption: AiChatbotCategoryOption;

  @Output() valueChange = new EventEmitter<string | string[]>();

  private restartSessionSubscription: Subscription;

  constructor(private aiChatbotService: AIChatbotService) {}

  ngOnInit(): void {
    if (this.isDisplayOnly && this.selectedValue) {
      this.selectedOption = this.options.find(
        (option) => option.value === this.selectedValue
      );
    }

    this.subscribeToRestartChat();
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.restartSessionSubscription) {
      this.restartSessionSubscription.unsubscribe();
      this.restartSessionSubscription = null;
    }
  }

  private subscribeToRestartChat(): void {
    // Unsubscribe from previous subscription if it exists
    if (this.restartSessionSubscription) {
      this.restartSessionSubscription.unsubscribe();
    }

    this.restartSessionSubscription =
      this.aiChatbotService.restartSession$.subscribe(() => {
        this.clearCategories();
      });
  }

  clearCategories() {
    this.selectedValue = undefined;
    this.selectedValues = new Set();
    this.selectedOption = undefined;
    this.valueChange.emit(undefined);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.menuTrigger.openMenu();
    }
  }

  onMenuItemKeydown(
    event: KeyboardEvent,
    option: AiChatbotCategoryOption
  ): void {
    if (event.key === 'Enter' || event.key === ' ') {
      if (this.isMultiSelect) {
        // For multi-select, prevent menu from closing and toggle the option
        event.preventDefault();
        event.stopPropagation();
        this.onOptionClick(option);
      }
      // For single select, allow default behavior (menu will close)
    }
  }

  onOptionClick(selectedOption: AiChatbotCategoryOption): void {
    if (this.isMultiSelect) {
      if (this.selectedValues.has(selectedOption.value)) {
        this.selectedValues.delete(selectedOption.value);
      } else {
        this.selectedValues.add(selectedOption.value);
      }
      this.valueChange.emit(Array.from(this.selectedValues));
      this.selectedOption = this.options.find(
        (option) => option.value === Array.from(this.selectedValues)[0]
      );
    } else {
      if (selectedOption.value === this.selectedValue) {
        this.selectedValue = undefined;
        this.selectedOption = undefined;
      } else {
        this.selectedValue = selectedOption.value;
        this.valueChange.emit(this.selectedValue);
        this.selectedOption = selectedOption;
      }
    }
  }

  isSelected(option: AiChatbotCategoryOption): boolean {
    if (this.isMultiSelect) {
      return this.selectedValues.has(option.value);
    } else {
      return option.value === this.selectedValue;
    }
  }
}
export { AiChatbotCategoryOption };
